import React from 'react';
import { Download, CheckCircle, Facebook } from 'lucide-react';

const DownloadCoverPage = () => {
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = '/cover.png';
    link.download = 'Visual_Creationz_Facebook_Cover.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRightClick = () => {
    const img = document.getElementById('coverImage');
    if (img) {
      img.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 py-20 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mb-4">
            🎨 Your Facebook Cover is Ready!
          </h1>
          <p className="text-xl text-gray-300">
            Visual Creationz - Professional Cover Photo
          </p>
        </div>

        {/* Preview Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8 border border-white/20 shadow-2xl">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <CheckCircle className="mr-3 text-green-400" size={32} />
            Preview
          </h2>
          
          {/* Cover Image */}
          <div className="bg-black/30 rounded-xl p-4 mb-6">
            <img 
              id="coverImage"
              src="/cover.png" 
              alt="Visual Creationz Facebook Cover" 
              className="w-full rounded-lg shadow-2xl"
            />
          </div>

          {/* Specs Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/5 rounded-lg p-4 text-center">
              <div className="text-purple-400 font-bold text-sm mb-1">Dimensions</div>
              <div className="text-gray-300 text-sm">820 x 312 px</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4 text-center">
              <div className="text-purple-400 font-bold text-sm mb-1">Format</div>
              <div className="text-gray-300 text-sm">PNG</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4 text-center">
              <div className="text-purple-400 font-bold text-sm mb-1">File Size</div>
              <div className="text-gray-300 text-sm">267 KB</div>
            </div>
            <div className="bg-white/5 rounded-lg p-4 text-center">
              <div className="text-purple-400 font-bold text-sm mb-1">Quality</div>
              <div className="text-gray-300 text-sm">High Resolution</div>
            </div>
          </div>
        </div>

        {/* Download Methods */}
        <h2 className="text-3xl font-bold text-white text-center mb-8">
          📥 Download Methods
        </h2>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* Method 1 */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:border-purple-400 transition-all hover:transform hover:scale-105 active:scale-95">
            <div className="text-4xl mb-4 text-center">⚡</div>
            <h3 className="text-xl font-bold text-purple-400 mb-3 text-center">
              Method 1: Direct Download
            </h3>
            <p className="text-gray-300 text-sm mb-4 text-center">
              Instant download with one click
            </p>
            <button
              onClick={handleDownload}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-700 text-white font-bold py-4 px-6 rounded-lg hover:from-purple-600 hover:to-purple-800 transition-all flex items-center justify-center"
            >
              <Download className="mr-2" size={20} />
              Download Now
            </button>
          </div>

          {/* Method 2 */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:border-pink-400 transition-all hover:transform hover:scale-105 active:scale-95">
            <div className="text-4xl mb-4 text-center">🖱️</div>
            <h3 className="text-xl font-bold text-pink-400 mb-3 text-center">
              Method 2: Right Click
            </h3>
            <p className="text-gray-300 text-sm mb-4 text-center">
              Right-click image, select "Save As"
            </p>
            <button
              onClick={handleRightClick}
              className="w-full bg-gradient-to-r from-pink-500 to-pink-700 text-white font-bold py-4 px-6 rounded-lg hover:from-pink-600 hover:to-pink-800 transition-all"
            >
              Go to Image
            </button>
          </div>

          {/* Method 3 */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:border-blue-400 transition-all hover:transform hover:scale-105 active:scale-95">
            <div className="text-4xl mb-4 text-center">🔗</div>
            <h3 className="text-xl font-bold text-blue-400 mb-3 text-center">
              Method 3: Direct Link
            </h3>
            <p className="text-gray-300 text-sm mb-4 text-center">
              Open in new tab and save
            </p>
            <a
              href="/cover.png"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full block bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold py-4 px-6 rounded-lg hover:from-blue-600 hover:to-blue-800 transition-all text-center"
            >
              Open Link
            </a>
          </div>
        </div>

        {/* Upload Instructions */}
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-l-4 border-purple-400 rounded-lg p-6 mb-8">
          <h3 className="text-2xl font-bold text-purple-400 mb-4 flex items-center">
            <Facebook className="mr-3" size={28} />
            How to Upload to Facebook
          </h3>
          <ol className="text-gray-200 space-y-3 list-decimal list-inside">
            <li>Download the cover photo using any method above</li>
            <li>Go to your Visual Creationz Facebook Business Page</li>
            <li>Click on your current cover photo (or "Add Cover Photo")</li>
            <li>Select "Upload Photo"</li>
            <li>Choose the downloaded file from your computer</li>
            <li>Adjust positioning if needed (should be perfect as-is)</li>
            <li>Click "Save Changes"</li>
          </ol>
        </div>

        {/* Pro Tips */}
        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-l-4 border-blue-400 rounded-lg p-6">
          <h3 className="text-2xl font-bold text-blue-400 mb-4">💡 Pro Tips</h3>
          <ul className="text-gray-200 space-y-2 list-disc list-inside">
            <li>Your logo is positioned on the LEFT to avoid being covered by your profile picture</li>
            <li>The bokeh lights on the right create depth and draw attention</li>
            <li>The dark, cinematic theme matches your website perfectly</li>
            <li>Facebook may compress the image slightly, but quality will remain high</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DownloadCoverPage;
